import React, { useRef, useState } from "react"

import * as styles from "../styles"
import Tooltip from "./Tooltip"

const NAME_MAX_LENGTH = 255
const TOOLTIP_SHOW_DELAY_MS = 400

interface RenameableNameProps {
  name: string
  label: "profile" | "batch"
  testId: string
  onRename: (name: string) => void // trimmed; "" means "back to the source name"
  suffix?: React.ReactNode // stays visible while editing
  children: React.ReactNode // the name as rendered at rest; clicking it opens the editor
}

const RenameableName: React.FC<RenameableNameProps> = ({ name, label, testId, onRename, suffix, children }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  // Escape and Enter both end the edit before the input's blur fires; this keeps blur from committing again.
  const settledRef = useRef(false)

  const startEditing = (event: React.MouseEvent) => {
    event.stopPropagation()
    settledRef.current = false
    setDraft(name)
    setIsEditing(true)
  }

  const commit = () => {
    if (settledRef.current) return
    settledRef.current = true
    setIsEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== name) onRename(trimmed)
  }

  const cancel = () => {
    settledRef.current = true
    setIsEditing(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") commit()
    if (event.key === "Escape") cancel()
  }

  if (isEditing) {
    return (
      <styles.RenameEditor>
        <styles.RenameInput
          autoFocus
          value={draft}
          maxLength={NAME_MAX_LENGTH}
          aria-label={`${label} name`}
          data-testid={`${testId}-input`}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          onClick={(event) => event.stopPropagation()}
        />
        {suffix}
        <styles.RenameHint>Enter to save · Esc to cancel</styles.RenameHint>
      </styles.RenameEditor>
    )
  }

  return (
    <>
      <Tooltip content="Click to rename" interactive={false} showDelayMs={TOOLTIP_SHOW_DELAY_MS}>
        <styles.RenameTrigger type="button" aria-label={`Rename ${label}`} data-testid={testId} onClick={startEditing}>
          {children}
        </styles.RenameTrigger>
      </Tooltip>
      {suffix}
    </>
  )
}

export default RenameableName
